import React, { useState } from 'react';
import {
  Box,
  Container,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import { Navbar } from '../navigation/Navbar';
import { Sidebar } from '../navigation/Sidebar';
import { MobileBottomNav } from '../navigation/MobileBottomNav';

const drawerWidth = 200;

interface AppLayoutProps {
  children: React.ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const muiTheme = useMuiTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.between('md', 'lg'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        background: (theme) => theme.palette.mode === 'light' 
          ? 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 25%, #F1F5F9 50%, #E6E3F7 75%, #F3E8FF 100%)'
          : theme.palette.background.default,
      }}
    >
      {/* Floating Navigation Bar */}
      <Navbar isMobile={isMobile} onMenuToggle={handleDrawerToggle} />

      {/* Desktop/Tablet Sidebar */}
      <Sidebar
        drawerWidth={drawerWidth}
        isMobile={isMobile}
        isTablet={isTablet}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Enhanced spacing to properly account for floating navbar
          pt: { 
            xs: '104px',  // Mobile: navbar (56px) + spacing (48px)
            sm: '112px',  // Tablet: navbar (64px) + spacing (48px)
            md: '120px',  // Desktop: navbar (64px) + spacing (56px)
          },
          pb: { xs: '72px', sm: 4, md: 4 }, // Extra bottom padding for mobile nav
          px: { xs: 2, sm: 3, md: 4 },
          ml: isMobile || isTablet ? 0 : `${drawerWidth}px`,
          minHeight: '100vh',
          // Ensure content doesn't get hidden behind navbar
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Container 
          maxWidth={false} 
          sx={{ 
            maxWidth: { lg: 1200, xl: 1400 },
            px: { xs: 0, sm: 0 }, // Remove extra padding, handled by main Box
            width: '100%',
          }}
        >
          {children}
        </Container>
      </Box>

      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav />}
    </Box>
  );
};